import type { ContentType } from "@amzn/g4sservice-client";
import type { AsyncStatus } from "@react-hookz/web";
import { produce } from "immer";

import type { SelectedContentInsight } from "../types/selectedContentInsight";

import { zustandStore } from "./zustand.helpers";

export interface ExploreWithAIState {
    prompt: string;
    isExamplePrompt: boolean;
    responseId: string;
    selectedContent: SelectedContentInsight[];
    asyncStatus: AsyncStatus;
    brandId?: string;
    browseNodeId?: string;
    contentTypes?: ContentType[];
}

export const initialState: ExploreWithAIState = {
    prompt: "",
    isExamplePrompt: false,
    responseId: "",
    selectedContent: [],
    asyncStatus: "not-executed",
    brandId: undefined,
    browseNodeId: undefined,
    contentTypes: [],
};

export interface ExploreWithAIActions {
    setPrompt: (prompt: string) => void;
    setIsExamplePrompt: (isExamplePrompt: boolean) => void;
    setResponseId: (responseId: string) => void;
    setSelectedContent: (selectedContent: SelectedContentInsight[]) => void;
    setAsyncStatus: (asyncStatus: AsyncStatus) => void;
    setBrandId: (brandId?: string) => void;
    setBrowseNodeId: (browseNodeId?: string) => void;
    setContentTypes: (contentTypes?: ContentType[]) => void;
    reset: () => void;
}

export const { store: exploreWithAiStore } = zustandStore<
    ExploreWithAIState,
    ExploreWithAIActions
>("ExploreWithAI", initialState, (set: any, get: any) => ({
    setPrompt: (prompt: string) =>
        set(() => {
            const state: ExploreWithAIState = get();
            return produce(state, (draft) => {
                draft.prompt = prompt;
                return draft;
            });
        }),
    setIsExamplePrompt: (isExamplePrompt: boolean) =>
        set(() => {
            const state: ExploreWithAIState = get();
            return produce(state, (draft) => {
                draft.isExamplePrompt = isExamplePrompt;
                return draft;
            });
        }),
    setResponseId: (responseId: string) =>
        set(() => {
            const state: ExploreWithAIState = get();
            return produce(state, (draft) => {
                draft.responseId = responseId;
                return draft;
            });
        }),
    setSelectedContent: (selectedContent: any) =>
        set(() => {
            const state: ExploreWithAIState = get();
            return produce(state, (draft) => {
                draft.selectedContent = selectedContent;
                return draft;
            });
        }),
    setAsyncStatus: (asyncStatus: AsyncStatus) =>
        set(() => {
            const state: ExploreWithAIState = get();
            return produce(state, (draft) => {
                draft.asyncStatus = asyncStatus;
                return draft;
            });
        }),
    setBrandId: (brandId?: string) =>
        set(() => {
            const state: ExploreWithAIState = get();
            return produce(state, (draft) => {
                draft.brandId = brandId;
                return draft;
            });
        }),
    setBrowseNodeId: (browseNodeId?: string) =>
        set(() => {
            const state: ExploreWithAIState = get();
            return produce(state, (draft) => {
                draft.browseNodeId = browseNodeId;
                return draft;
            });
        }),
    setContentTypes: (contentTypes: any) =>
        set(() => {
            const state: ExploreWithAIState = get();
            return produce(state, (draft) => {
                draft.contentTypes = contentTypes;
                return draft;
            });
        }),
    reset: () => set(() => ({ ...initialState })),
}));
